import { useCallback, useEffect, useState } from 'react'
import { notifications } from '@mantine/notifications'
import type { MedplumClient } from '@medplum/core'
import type {
  Bundle,
  BundleEntry,
  Coverage,
  CoverageEligibilityRequest,
  CoverageEligibilityResponse,
  DocumentReference,
  Patient,
} from '@medplum/fhirtypes'
import type { ApiClient, components } from '@behuman.corp/contracts'
import type {
  CoverageTemplate,
  InsuranceCardReferenceMap,
  InsuranceCardSide,
  InsuranceRank,
} from '@/types/insuranceTypes'
import { getDocumentReferencePointer } from '@/utils/insuranceCardUtils'
import {
  getCoveragePlanSnapshot,
  getCoverageState,
  getSubscriberDob,
  getSubscriberMemberId,
  getSubscriberName,
  normalizeResolvedCoverageSlots,
} from '@/utils/coverageUtils'
import { uploadInsuranceImage } from '@/utils/documentReferenceUtils'
import { logError } from '@/utils/errorUtils'
import { useInvalidateInsuranceVerdicts } from '@/hooks/useInsuranceVerdicts'
import type { InsuranceDisplayData, InsurancePayerOption } from './manageCoverages.types'

type ResolvedCoverageSlots = {
  primary?: Coverage
  secondary?: Coverage
  tertiary?: Coverage
}

type ContractUpsertPatientCoverageResult = components['schemas']['UpsertPatientCoverageResultDto']
type ContractDeletePatientCoverageResult = components['schemas']['DeletePatientCoverageResultDto']
type ContractDeleteCoverageInsuranceCardResult =
  components['schemas']['DeleteCoverageInsuranceCardResultDto']

type UseCoveragePersistenceOptions = {
  medplum: MedplumClient
  middlewareClient: ApiClient
  patientId?: string
}

export function useCoveragePersistence({
  medplum,
  middlewareClient,
  patientId,
}: UseCoveragePersistenceOptions) {
  const invalidateInsuranceVerdicts = useInvalidateInsuranceVerdicts()
  const [existingCoverages, setExistingCoverages] = useState<Coverage[]>([])
  const [organizations, setOrganizations] = useState<InsurancePayerOption[]>([])
  const [patientResource, setPatientResource] = useState<Patient | null>(null)
  const [hasFetchedCoverages, setHasFetchedCoverages] = useState(false)

  const toResolvedCoverageSlots = (
    resolved:
      | components['schemas']['ResolvedPatientCoveragesResultDto']
      | ContractUpsertPatientCoverageResult
      | ContractDeletePatientCoverageResult
      | ContractDeleteCoverageInsuranceCardResult,
  ): ResolvedCoverageSlots => ({
    primary: resolved.primary as Coverage | undefined,
    secondary: resolved.secondary as Coverage | undefined,
    tertiary: resolved.tertiary as Coverage | undefined,
  })

  const applyResolvedCoverages = (resolved: ResolvedCoverageSlots) => {
    setExistingCoverages(normalizeResolvedCoverageSlots(resolved))
  }

  const getInsuranceDisplayData = (coverage: Coverage): InsuranceDisplayData => {
    const plan =
      coverage.class?.find((entry) => entry.type?.coding?.some((coding) => coding.code === 'plan'))
        ?.value ||
      coverage.payor?.[0]?.display ||
      ''
    const group =
      coverage.class?.find((entry) => entry.type?.coding?.some((coding) => coding.code === 'group'))
        ?.value || ''
    const MEMBER_ID_SYSTEM = 'https://behuman.health/fhir/coverage-member-id'
    const memberIdValue =
      coverage.identifier?.find((identifier) => identifier.system === MEMBER_ID_SYSTEM)?.value ||
      coverage.identifier?.find((identifier) => !identifier.system)?.value ||
      ''
    const memberId = memberIdValue.length > 30 ? '' : memberIdValue
    const payorName = coverage.payor?.[0]?.display || ''
    const payerId = coverage.payor?.[0]?.identifier?.value || ''

    return { plan, group, memberId, payorName, payerId }
  }

  const fetchOrganizations = async () => {
    try {
      const { result } = await middlewareClient.get('/insurance/payers-list')
      setOrganizations(
        result.map((item) => ({
          id: item.id,
          payerId: item.payerId,
          payerName: item.payerName,
          state: item.state,
          source: item.source,
          editable: item.editable,
          deletable: item.deletable,
          parameterId: item.parameterId,
          label: `${item.payerName} - ${item.state}`,
        })),
      )
    } catch (error) {
      logError('insurance', 'Failed to fetch insurance payer organizations', error)
    }
  }

  const fetchCoverages = async (effectivePatientId: string) => {
    try {
      const { result } = await middlewareClient.get('/insurance/patient/{id}/coverages', {
        id: effectivePatientId,
      })
      applyResolvedCoverages(toResolvedCoverageSlots(result))
    } catch (error) {
      logError('insurance', 'Failed to fetch insurance coverages', error)
      notifications.show({
        title: 'Error',
        message: 'Failed to fetch insurance coverages',
        color: 'red',
      })
    } finally {
      if (effectivePatientId === patientId) {
        setHasFetchedCoverages(true)
      }
    }
  }

  const ensurePatientResource = async (effectivePatientId: string): Promise<Patient> => {
    if (patientResource?.id === effectivePatientId) {
      return patientResource
    }

    const patient = await medplum.readResource('Patient', effectivePatientId)
    setPatientResource(patient)
    return patient
  }

  const upsertCoverageForPatient = async (
    effectivePatientId: string,
    template: CoverageTemplate,
    options?: {
      coverageId?: string
      references?: InsuranceCardReferenceMap
      applyResolvedResult?: boolean
    },
  ): Promise<Coverage> => {
    const { result } = await middlewareClient.post('/insurance/patient/{id}/coverages/upsert', {
      id: effectivePatientId,
      coverageId: options?.coverageId,
      rank: template.insuranceRank,
      memberId: template.memberId,
      groupId: template.groupId || undefined,
      payerId: template.payorId,
      payerName: template.payorName,
      coverageState: template.coverageState,
      planName: template.planName,
      productType: template.productType,
      planId: template.planId,
      lobCategory: template.lobCategory,
      coverageType: template.coverageType,
      subscriberName: template.subscriberName || undefined,
      subscriberDob: template.subscriberDob || undefined,
      subscriberMemberId: template.subscriberMemberId || undefined,
      frontInsuranceCardDocumentReference: options?.references?.front,
      backInsuranceCardDocumentReference: options?.references?.back,
    })

    if (options?.applyResolvedResult !== false) {
      applyResolvedCoverages(toResolvedCoverageSlots(result))
    }

    return result.coverage as unknown as Coverage
  }

  const attachInsuranceDocumentsToCoverage = async (
    effectivePatientId: string,
    coverage: Coverage,
    rank: InsuranceRank,
    documents: Partial<Record<InsuranceCardSide, DocumentReference>>,
    applyResolvedResult = true,
  ): Promise<Coverage> => {
    const referenceUpdates: InsuranceCardReferenceMap = {}
    const entries = Object.entries(documents) as [
      InsuranceCardSide,
      DocumentReference | undefined,
    ][]

    entries.forEach(([side, document]) => {
      const pointer = document ? getDocumentReferencePointer(document) : undefined
      if (pointer) {
        referenceUpdates[side] = pointer
      }
    })

    if (!Object.keys(referenceUpdates).length) {
      return coverage
    }

    const coverageTypeCode = coverage.relationship?.coding?.[0]?.code || ''
    const normalizedCoverageType =
      coverageTypeCode === '18' ? 'self' : coverageTypeCode === 'G8' ? 'other' : coverageTypeCode
    const coverageDisplay = getInsuranceDisplayData(coverage)
    const planSnapshot = getCoveragePlanSnapshot(coverage)

    return upsertCoverageForPatient(
      effectivePatientId,
      {
        id: coverage.id || `temp-${Date.now()}`,
        memberId: coverageDisplay.memberId,
        groupId: coverageDisplay.group,
        insuranceRank: rank,
        coverageType: normalizedCoverageType,
        payorId: coverage.payor?.[0]?.identifier?.value || '',
        payorName: coverageDisplay.payorName,
        coverageState: getCoverageState(coverage),
        planName: planSnapshot?.planName,
        productType: planSnapshot?.productType,
        planId: planSnapshot?.planId,
        lobCategory: planSnapshot?.lobCategory,
        subscriberName: getSubscriberName(coverage),
        subscriberDob: getSubscriberDob(coverage),
        subscriberMemberId: getSubscriberMemberId(coverage),
      },
      {
        coverageId: coverage.id,
        references: referenceUpdates,
        applyResolvedResult,
      },
    )
  }

  const uploadInsuranceCardsForCoverage = async (
    effectivePatientId: string,
    coverage: Coverage,
    rank: InsuranceRank,
    files: Partial<Record<InsuranceCardSide, File>>,
    validateCardSelection: (existingFront?: string, existingBack?: string) => boolean,
    suppressSuccessNotification = false,
    applyResolvedResult = true,
  ): Promise<Coverage | null> => {
    const frontFile = files.front
    const backFile = files.back
    const hasNewCardSelection = Boolean(frontFile || backFile)

    if (!hasNewCardSelection) {
      return coverage
    }

    const existingReferences = coverage.extension
      ? {
          front: coverage.extension.find(
            (ext) => ext.url === 'https://behuman.health/coverage-insurance-card-front',
          )?.valueReference?.reference,
          back: coverage.extension.find(
            (ext) => ext.url === 'https://behuman.health/coverage-insurance-card-back',
          )?.valueReference?.reference,
        }
      : {}

    if (!validateCardSelection(existingReferences.front, existingReferences.back)) {
      return null
    }

    try {
      const patient = await ensurePatientResource(effectivePatientId)
      const uploads: Promise<[InsuranceCardSide, DocumentReference]>[] = []
      const sidesUploaded: InsuranceCardSide[] = []

      if (frontFile) {
        sidesUploaded.push('front')
        uploads.push(
          uploadInsuranceImage(medplum, frontFile, 'front', patient, rank).then((doc) => [
            'front',
            doc,
          ]),
        )
      }

      if (backFile) {
        sidesUploaded.push('back')
        uploads.push(
          uploadInsuranceImage(medplum, backFile, 'back', patient, rank).then((doc) => [
            'back',
            doc,
          ]),
        )
      }

      const uploadedDocs: Partial<Record<InsuranceCardSide, DocumentReference>> = {}
      const results = await Promise.all(uploads)
      results.forEach(([side, document]) => {
        uploadedDocs[side] = document
      })

      const updatedCoverage = await attachInsuranceDocumentsToCoverage(
        effectivePatientId,
        coverage,
        rank,
        uploadedDocs,
        applyResolvedResult,
      )

      if (!suppressSuccessNotification) {
        notifications.show({
          title: 'Insurance cards updated',
          message:
            sidesUploaded.length === 2
              ? 'Front and back of the insurance card were uploaded successfully.'
              : sidesUploaded[0] === 'front'
                ? 'Front of the insurance card was uploaded successfully.'
                : 'Back of the insurance card was uploaded successfully.',
          color: 'green',
        })
      }

      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('insurance-card-uploaded', {
            detail: { patientId: effectivePatientId, rank },
          }),
        )
      }

      return updatedCoverage
    } catch (error) {
      logError('insurance', 'Failed to upload insurance card images', error)
      notifications.show({
        title: 'Upload failed',
        message: (error as Error)?.message || 'Unable to upload insurance images right now.',
        color: 'red',
      })
      return null
    }
  }

  const deleteCoverage = async (effectivePatientId: string, rank: InsuranceRank) => {
    const { result } = await middlewareClient.delete('/insurance/patient/{id}/coverages/{rank}', {
      id: effectivePatientId,
      rank,
    })
    applyResolvedCoverages(toResolvedCoverageSlots(result))
    return result
  }

  const deleteInsuranceCardImage = async (
    effectivePatientId: string,
    rank: InsuranceRank,
    side: InsuranceCardSide,
  ) => {
    const { result } = await middlewareClient.delete(
      '/insurance/patient/{id}/coverages/{rank}/card/{side}',
      {
        id: effectivePatientId,
        rank,
        side,
      },
    )
    applyResolvedCoverages(toResolvedCoverageSlots(result))
    return result
  }

  const checkInsurance = async (effectivePatientId: string, coverageId: string) => {
    const response = (await middlewareClient.post('/insurance/check-eligibility', {
      patientId: effectivePatientId,
      coverageId,
    })) as { result: CoverageEligibilityResponse }
    // A new check changes this patient's insurance verdict wherever the console shows it.
    void invalidateInsuranceVerdicts()
    return response
  }

  const fetchLatestEligibilityResponseForCoverage = useCallback(
    async (
      effectivePatientId: string,
      coverageId: string,
    ): Promise<CoverageEligibilityResponse | null> => {
      const patientReference = `Patient/${effectivePatientId}`
      const coverageReference = `Coverage/${coverageId}`
      let nextUrl: string | undefined = `patient=${patientReference}&_sort=-_lastUpdated&_count=100`
      let latestRequest: CoverageEligibilityRequest | undefined

      while (nextUrl && !latestRequest) {
        const bundle: Bundle = await medplum.search('CoverageEligibilityRequest', nextUrl)
        const requests =
          bundle.entry
            ?.map((entry: BundleEntry) => entry.resource)
            .filter(Boolean)
            .filter((resource): resource is CoverageEligibilityRequest => {
              return resource?.resourceType === 'CoverageEligibilityRequest'
            }) ?? []

        latestRequest = requests.find((request) =>
          request.insurance?.some(
            (insurance) => insurance.coverage?.reference === coverageReference,
          ),
        )

        const nextLink = bundle.link?.find((link) => link.relation === 'next')?.url
        nextUrl = nextLink ? nextLink.replace(medplum.getBaseUrl(), '') : undefined
      }

      if (!latestRequest?.id) {
        return null
      }

      const requestReference = `CoverageEligibilityRequest/${latestRequest.id}`

      return (
        (await medplum.searchOne(
          'CoverageEligibilityResponse',
          `request=${requestReference}&_sort=-_lastUpdated`,
        )) ?? null
      )
    },
    [medplum],
  )

  useEffect(() => {
    void fetchOrganizations()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (patientId) {
      setHasFetchedCoverages(false)
      void fetchCoverages(patientId)
      return
    }

    setExistingCoverages([])
    setPatientResource(null)
    setHasFetchedCoverages(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId])

  useEffect(() => {
    if (!patientId) {
      return
    }

    let isMounted = true
    medplum
      .readResource('Patient', patientId)
      .then((patient) => {
        if (isMounted) {
          setPatientResource(patient)
        }
      })
      .catch((error) => {
        logError('insurance', 'Failed to fetch patient resource', error)
      })

    return () => {
      isMounted = false
    }
  }, [medplum, patientId])

  return {
    applyResolvedCoverages,
    checkInsurance,
    deleteCoverage,
    deleteInsuranceCardImage,
    ensurePatientResource,
    existingCoverages,
    fetchLatestEligibilityResponseForCoverage,
    fetchCoverages,
    getInsuranceDisplayData,
    hasFetchedCoverages,
    organizations,
    patientResource,
    fetchOrganizations,
    setPatientResource,
    upsertCoverageForPatient,
    uploadInsuranceCardsForCoverage,
  }
}
